import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Sparkles, Activity, Zap, Dumbbell, Moon, BookOpen, MessageSquare, Search, ArrowRight } from 'lucide-react';

const STACKS = [
  {
    id: 'wolverine',
    icon: Activity,
    name: 'Wolverine Stack',
    tagline: 'A blended recovery protocol for people who want broader healing, faster recovery, and better physical resilience.',
    includes: 'BPC-157 + TB-500',
    bestFor: 'People with multiple recovery concerns, gym injuries, joint/tendon issues, athletes, active adults, or anyone who feels like their body takes too long to bounce back',
    primaryBenefit: 'Comprehensive recovery and tissue-support protocol',
    color: 'from-orange-500/10 to-amber-500/5 border-orange-500/20',
    accent: 'text-orange-400',
  },
  {
    id: 'mitochondrial',
    icon: Zap,
    name: 'Mitochondrial Stack',
    tagline: 'For people who feel older than they should — and want their energy back.',
    includes: 'NAD+ + MOTS-c + Glutathione',
    bestFor: 'People experiencing significant fatigue, brain fog, or the feeling that recovery and energy "just aren\'t what they used to be"',
    primaryBenefit: 'Cellular energy production, mitochondrial health, oxidative stress reduction',
    color: 'from-cyan-500/10 to-blue-500/5 border-cyan-500/20',
    accent: 'text-cyan-400',
  },
  {
    id: 'body-composition',
    icon: Dumbbell,
    name: 'Body Composition Stack',
    tagline: 'For people who want to lose fat and build muscle at the same time — not just a lower number on the scale.',
    includes: 'GLP-1 (Semaglutide or Tirzepatide) + Tesamorelin or Sermorix + Protein protocol guidance',
    bestFor: 'People focused on how their body looks and performs, not just scale weight. People on GLP-1s who want to preserve or build muscle.',
    primaryBenefit: 'Fat loss + muscle preservation/growth simultaneously',
    color: 'from-purple-500/10 to-violet-500/5 border-purple-500/20',
    accent: 'text-purple-400',
  },
  {
    id: 'sleep',
    icon: Moon,
    name: 'Sleep Optimization Stack',
    tagline: "For people whose sleep has never fully recovered — and who know everything else depends on fixing it first.",
    includes: 'NAD+ + Sermorelin',
    bestFor: 'People with chronic poor sleep, people whose sleep quality has declined with age, fatigue that adequate sleep hours don\'t resolve',
    primaryBenefit: 'Deep sleep restoration, growth hormone pulse optimization, overnight recovery',
    color: 'from-indigo-500/10 to-blue-500/5 border-indigo-500/20',
    accent: 'text-indigo-400',
  },
];

const StackFinderPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#020817] text-white">
      <Helmet>
        <title>Find Your Stack | MyWeightForecast</title>
        <meta name="description" content="Pre-built optimization protocols designed around specific goals. Find the peptide stack that fits your needs." />
      </Helmet>

      {/* Nav */}
      <header className="border-b border-slate-800/50 backdrop-blur-xl bg-slate-950/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 border border-cyan-500/30 flex items-center justify-center">
              <span className="text-lg font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">B</span>
            </div>
            <span className="font-heading text-xl font-bold text-white">Bryan 2.0</span>
          </Link>
          <nav className="flex items-center gap-4 sm:gap-6">
            <Link to="/blog" className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-2">
              <BookOpen className="h-4 w-4" /><span className="hidden sm:inline">Blog</span>
            </Link>
            <a href="sms:8016518363" className="text-slate-400 hover:text-cyan-400 transition-colors hidden sm:flex items-center gap-2">
              <MessageSquare className="h-4 w-4" /><span>Text Bryan</span>
            </a>
            <Button onClick={() => navigate('/forecast')} className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white rounded-full px-6 py-2 text-sm font-bold transition-all" data-testid="nav-cta-btn">
              AI Forecast
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-16 pb-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-4" data-testid="stack-finder-title">Find Your Stack</h1>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Pre-built optimization protocols designed around specific goals. Each stack combines therapies that work through different mechanisms — so they complement each other instead of overlapping.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stack Cards */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-6 space-y-6">
          {STACKS.map((stack, idx) => (
            <motion.div
              key={stack.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className={`bg-gradient-to-br ${stack.color} border rounded-2xl p-6 sm:p-8 backdrop-blur-xl`}
              data-testid={`stack-card-${stack.id}`}
            >
              <div className="flex items-start gap-4 mb-4">
                <stack.icon className={`h-8 w-8 ${stack.accent} shrink-0`} strokeWidth={1.5} />
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white">{stack.name}</h2>
                  <p className="text-slate-300 mt-1">{stack.tagline}</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-4 mb-6 text-sm">
                <div>
                  <span className="text-slate-500 block mb-1">Includes</span>
                  <span className="text-slate-300">{stack.includes}</span>
                </div>
                <div>
                  <span className="text-slate-500 block mb-1">Best for</span>
                  <span className="text-slate-300">{stack.bestFor}</span>
                </div>
                <div>
                  <span className="text-slate-500 block mb-1">Primary benefit</span>
                  <span className={`font-medium ${stack.accent}`}>{stack.primaryBenefit}</span>
                </div>
              </div>

              <a
                href="sms:8016518363"
                className={`inline-flex items-center gap-2 text-sm font-medium ${stack.accent} hover:underline`}
              >
                Text Bryan about this stack <ArrowRight className="h-4 w-4" />
              </a>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Not Sure CTA */}
      <section className="pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center bg-slate-950/60 border border-cyan-500/20 rounded-2xl p-8">
            <Search className="h-8 w-8 text-cyan-400 mx-auto mb-4" />
            <h3 className="font-heading text-2xl font-bold mb-3">Not Sure Which Stack Fits You?</h3>
            <p className="text-slate-400 mb-6">The AI Forecast takes 3 minutes and tells you exactly which therapies and stacks match your specific profile.</p>
            <Button
              onClick={() => navigate('/forecast')}
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white rounded-full px-8 py-4 text-lg font-bold transition-all"
              data-testid="stack-forecast-cta"
            >
              <Sparkles className="mr-2 h-5 w-5" />
              Build My AI Forecast
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StackFinderPage;
